import {
  BoxExpand,
  Button,
  ButtonsWrapper,
  Form,
  PLATFORM_THEME_COLORS,
  TextInput,
  Typography,
  ColorSelect,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';
import { DEFAULT_NAME_FIELD_REGEX } from '@thinknet-gmbh/platform-common';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';

import BoxExpandButtons from 'src/pages/settings/components/company-settings/components/common-box-expand/components/box-expand-buttons.component';
import {
  FormWrapper,
  InfoContainer,
} from 'src/pages/settings/components/company-settings/components/common-box-expand/common-box-expand.styles';
import { USER_POSITION_LABELS } from 'src/pages/settings/components/company-settings/components/user-position/user-position.constants';

import CommonBoxExpandEdit from './components/common-box-expand-edit.component';
import { ICommonBoxExpandProps } from './common-box-expand.interfaces';

const CommonBoxExpand: FC<ICommonBoxExpandProps> = ({
  data,
  formItemLabel,
  formItemName,
  formListName,
  createButtonTxt,
  requiredMessage,
  inputPlaceholder,
  boxExpandTitle,
  colorSelectLabel,
  colorSelectPlaceholder,
  onSubmitCreateForm,
  colorSelectName,
  separator,
  onSubmitEditForm,
  onSubmitDelete,
  canCreate,
  loading,
  canEdit,
  canDelete,
}) => {
  const { t } = useTranslation(['common', 'settings']);
  const [form] = useForm();
  const [isOpenCreatePosition, setIsOpenCreatePosition] = useState(false);
  const [positionName, setPositionName] = useState('');
  const [absenceReasons, setAbsenceReasons] = useState<{
    name?: string;
    color?: string;
  }>({});
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isValidatedColorSelect, validateColorSelect] = useState(true);

  useEffect(() => {
    if (formItemName === USER_POSITION_LABELS.positionName) {
      setFilteredData(data.filter((obj: any) => obj.name !== 'Project Owner'));
    }
  }, [data, formItemName]);

  useEffect(() => {
    form.setFieldsValue({ [formListName]: filteredData });
  }, [form, formListName, filteredData]);

  const onSubmitForm = (values: any) => {
    onSubmitEditForm(values);
    setIsOpenCreatePosition(!isValidatedColorSelect);
    setIsEdit(false);

    if (positionName) {
      onSubmitCreateForm(positionName);
    }
  };

  const addPositionName = () => {
    if (!absenceReasons.color) {
      validateColorSelect(false);
    }
    if (colorSelectName && absenceReasons.color && absenceReasons.name) {
      onSubmitCreateForm(absenceReasons);
      setIsOpenCreatePosition(false);
      setAbsenceReasons({});
      setFilteredData([]);
      return;
    }
    if (!positionName || !/^[A-Za-z ]+$/.test(positionName)) {
      return;
    }
    onSubmitCreateForm(positionName);
    setIsOpenCreatePosition(false);
    setPositionName('');
  };

  const onCloseEdit = () => {
    form.resetFields();
    setIsEdit(false);
    setIsOpenCreatePosition(false);
  };

  const onCancelCreate = () => {
    setIsOpenCreatePosition(false);
    setAbsenceReasons({});
    validateColorSelect(true);
    setPositionName('');
  };

  return (
    <FormWrapper>
      {loading && <Loader />}
      <Form form={form} layout="vertical" onFinish={onSubmitForm}>
        <Form.List name={formListName} initialValue={filteredData}>
          {(fields, { add, remove }) => (
            <BoxExpand
              title={boxExpandTitle}
              separator={separator}
              buttons={
                <BoxExpandButtons
                  isEdit={isEdit}
                  canCreate={canCreate}
                  canEdit={canEdit}
                  createButtonTxt={createButtonTxt}
                  createElement={() => {
                    if (isEdit) {
                      add();
                      return;
                    }
                    setIsOpenCreatePosition(true);
                  }}
                  openEdit={() => setIsEdit(true)}
                />
              }
            >
              {isEdit ? (
                <CommonBoxExpandEdit
                  data={filteredData}
                  inputPlaceholder={inputPlaceholder}
                  formItemLabel={formItemLabel}
                  formItemName={formItemName}
                  colorSelectLabel={colorSelectLabel}
                  colorSelectName={colorSelectName}
                  requiredMessage={requiredMessage}
                  remove={remove}
                  fields={fields}
                  closeEdit={onCloseEdit}
                  onSubmitDelete={onSubmitDelete}
                />
              ) : (
                <>
                  <InfoContainer>
                    {data.map((item, index) => (
                      <Typography key={index} fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                        {item[formItemName]}
                      </Typography>
                    ))}
                  </InfoContainer>
                  {isOpenCreatePosition && (
                    <>
                      <Form.Item
                        name={formItemName}
                        label={formItemLabel}
                        rules={[
                          {
                            required: true,
                            message: requiredMessage,
                          },
                          {
                            pattern: DEFAULT_NAME_FIELD_REGEX,
                            message: t('common:validation.error.incorrectFormat'),
                          },
                        ]}
                      >
                        <TextInput
                          placeholder={inputPlaceholder}
                          onChange={e => {
                            if (colorSelectLabel) {
                              setAbsenceReasons(oldValue => ({
                                ...oldValue,
                                name: e.target.value,
                              }));
                              return;
                            }
                            setPositionName(e.target.value);
                          }}
                          value={positionName}
                          maxLength={50}
                        />
                      </Form.Item>
                      {colorSelectName && (
                        <Form.Item
                          label={colorSelectLabel}
                          help={
                            isValidatedColorSelect
                              ? ''
                              : t('common:validation.error.incorrectFormat')
                          }
                          validateStatus={isValidatedColorSelect ? 'validating' : 'error'}
                          required
                        >
                          <ColorSelect
                            translate={t}
                            placeholder={colorSelectPlaceholder}
                            value={absenceReasons.color}
                            onChange={value => {
                              setAbsenceReasons(oldValue => ({ ...oldValue, color: value }));
                              validateColorSelect(true);
                            }}
                          />
                        </Form.Item>
                      )}
                      <ButtonsWrapper>
                        <Button type="primary" onClick={addPositionName} htmlType="submit">
                          {t('settings:company.buttons.add')}
                        </Button>
                        <Button onClick={onCancelCreate}>{t('common:buttons.cancel')}</Button>
                      </ButtonsWrapper>
                    </>
                  )}
                </>
              )}
            </BoxExpand>
          )}
        </Form.List>
      </Form>
    </FormWrapper>
  );
};

export default CommonBoxExpand;
